import {
  AccountOutcome,
  AccountStatusOutcome,
  ActivateUser,
  CreateAccountOutcome,
  CreateActivateUser,
} from "../Types/Users";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { pullAllUsers } from "../Services/API/UserManagement";
import {
  activateAccount,
  createAccount,
  deactivateAccount,
  deleteAccount,
  editAccount,
  getAccountStatus,
  resendInvite,
  resetPasswordForUser,
} from "../Services/API/UserAccountActions";
import { PaginationRequest, PaginationResponse } from "../Types/Pagination";

/**
 * @description Gets all Users for a Base Client
 * @returns UseQueryResult<IUser[]>
 */
export function useGetAllUsersQuery(requestBody: PaginationRequest, clientId?: string) {
  return useQuery<PaginationResponse<ActivateUser>>(
    ["activateUsers", { requestBody, clientId }],
    async () => {
      const response = await pullAllUsers(requestBody, clientId);
      return response;
    },
    { staleTime: 1000 * 60 * 3 } // 3 minutes
  );
}

/**
 * @description Creates a new User and reports whether the invite email went out
 * @queryInvalidations ["activateUsers"] when the user was created
 */
export function useCreateActivateUserMutation() {
  const queryClient = useQueryClient();

  return useMutation<CreateAccountOutcome, unknown, CreateActivateUser>(
    (userData) => {
      return createAccount(userData);
    },
    {
      onSuccess: (outcome) => {
        if (outcome.created) {
          return queryClient.invalidateQueries({ queryKey: ["activateUsers"], exact: false });
        }
      },
      mutationKey: ["createActivateUser"],
    }
  );
}

/**
 * @description Gets whether a User has an Auth0 account and has ever logged in
 */
export function useAccountStatusQuery(userId: string | undefined) {
  return useQuery<AccountStatusOutcome>(["accountStatus", { userId }], () => getAccountStatus(userId!), {
    enabled: !!userId,
    retry: false,
  });
}

// Account actions resolve { ok: false } instead of rejecting, so invalidation checks outcome.ok.
function useUsersListMutation<TVariables>(mutationFn: (variables: TVariables) => Promise<AccountOutcome>) {
  const queryClient = useQueryClient();

  return useMutation<AccountOutcome, unknown, TVariables>(mutationFn, {
    onSuccess: (outcome) => {
      if (outcome.ok) {
        return queryClient.invalidateQueries({ queryKey: ["activateUsers"], exact: false });
      }
    },
  });
}

function useAccountStatusMutation(mutationFn: (userId: string) => Promise<AccountOutcome>) {
  const queryClient = useQueryClient();

  return useMutation<AccountOutcome, unknown, string>(mutationFn, {
    onSuccess: (outcome, userId) => {
      if (outcome.ok) {
        // Not awaited, so the outcome shows without waiting on the status refetch.
        queryClient.invalidateQueries({ queryKey: ["accountStatus", { userId }] });
      }
    },
  });
}

/**
 * @description Saves a User's name, email, and phone
 * @queryInvalidations ["activateUsers"] when saved
 */
export function useEditAccountMutation() {
  return useUsersListMutation(({ userId, fields }: { userId: string; fields: CreateActivateUser }) =>
    editAccount(userId, fields)
  );
}

/**
 * @description Activates a User
 * @queryInvalidations ["activateUsers"] when activated
 */
export function useActivateAccountMutation() {
  return useUsersListMutation((userId: string) => activateAccount(userId));
}

/**
 * @description Deactivates a User
 * @queryInvalidations ["activateUsers"] when deactivated
 */
export function useDeactivateAccountMutation() {
  return useUsersListMutation((userId: string) => deactivateAccount(userId));
}

/**
 * @description Deletes a User
 * @queryInvalidations ["activateUsers"] when deleted
 */
export function useDeleteAccountMutation() {
  return useUsersListMutation((userId: string) => deleteAccount(userId));
}

/**
 * @description Sends a User a new invite email
 * @queryInvalidations ["accountStatus", { userId }] when sent
 */
export function useResendInviteMutation() {
  return useAccountStatusMutation((userId) => resendInvite(userId));
}

/**
 * @description Sends a User a password reset, or an invite when they have no login
 * @queryInvalidations ["accountStatus", { userId }] when sent
 */
export function useResetPasswordForUserMutation() {
  return useAccountStatusMutation((userId) => resetPasswordForUser(userId));
}
